import { useState, useEffect } from "react";
import axios from "axios";
import { ClipboardList, Flag, Users } from "lucide-react";
import { formatDate } from "../../lib/date";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fetch open/overdue linked-action counts grouped by link_id for row badges.
export function useLinkedActionCounts(linkType) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    axios.get(`${API}/meetings/linked-action-counts`, { params: { link_type: linkType } })
      .then((r) => setCounts(r.data.counts || {})).catch(() => {});
  }, [linkType]);
  return counts;
}

// Small badge showing a record's open management-action count (red if overdue).
export function LinkedActionBadge({ info, testid }) {
  if (!info || !info.open) return null;
  const overdue = info.overdue > 0;
  return (
    <span title={`${info.open} open management action${info.open === 1 ? "" : "s"}${overdue ? ` · ${info.overdue} overdue` : ""}`}
      className={`inline-flex items-center gap-1 rounded-full border-0 px-2 py-0.5 text-[10px] shrink-0 ${overdue ? "bg-[#FFE9E0] text-[#C23A00]" : "bg-[#FFF8D6] text-[#8A6D00]"}`}
      data-testid={testid}>
      <ClipboardList className="w-3 h-3" /> {info.open}
    </span>
  );
}

const S = {
  open: { label: "Open", cls: "text-[#6B5600]" },
  in_progress: { label: "In progress", cls: "text-[#8A6D00]" },
  done: { label: "Done", cls: "text-[#0E6B3F]" },
};

// Read-only list of management-meeting action items linked to a client/prospect.
export default function LinkedActions({ linkType, linkId }) {
  const [data, setData] = useState({ actions: [], open: 0 });

  useEffect(() => {
    if (!linkId) return;
    axios.get(`${API}/meetings/linked-actions`, { params: { link_type: linkType, link_id: linkId } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [linkType, linkId]);

  if (!data.actions.length) return null;

  return (
    <div data-testid="linked-actions">
      <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-2 flex items-center gap-1.5">
        <ClipboardList className="w-3.5 h-3.5 text-[#8A6D00]" /> Management actions ({data.open} open)
      </div>
      <div className="space-y-1.5">
        {data.actions.map((a) => (
          <div key={a.id} className={`rounded border px-3 py-2 ${a.overdue ? "border-[#FFE9E0] bg-[#FFE9E0]" : "border-[#DAD5CA] bg-[#FFFFFF]"}`} data-testid={`linked-action-${a.id}`}>
            <div className="text-sm text-[#1A1A1A]">{a.description}</div>
            <div className="text-xs text-[#8A857A] flex items-center gap-2 mt-0.5">
              <span className={S[a.status]?.cls}>{S[a.status]?.label || a.status}</span>
              {a.owner_name && <span><Users className="w-3 h-3 inline mr-0.5" />{a.owner_name}</span>}
              {a.due_date && <span className={a.overdue ? "text-[#C23A00]" : ""}><Flag className="w-3 h-3 inline mr-0.5" />{formatDate(a.due_date)}{a.overdue ? " · overdue" : ""}</span>}
              <span className="text-[#8A857A]">· from {formatDate(a.meeting_date)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
